import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const PRIMARY_AREAS = [
  {
    name: 'Beverly Hills',
    subtitle: 'Our Home Market',
    description: 'The epicenter of luxury residential design in Southern California — home to our studio at 215 N La Peer Dr.',
    image: 'https://public.readdy.ai/ai/img_res/0bc299ccdad962eed256380814716577.jpg',
  },
  {
    name: 'Los Angeles',
    subtitle: 'Citywide Design',
    description: 'Residential and commercial projects spanning the city — from hillside estates to Westside condominiums.',
    image: 'https://readdy.ai/api/search-image?query=modern%20los%20angeles%20luxury%20home%20interior%20open%20plan%20living%20room%20warm%20stone%20floor%20contemporary%20california%20lifestyle%20editorial%20bright%20clean%20neutral%20warm%20tones%20sophisticated%20minimal&width=800&height=600&seq=la01&orientation=landscape',
  },
  {
    name: 'Bel Air',
    subtitle: 'Ultra-Luxury Estates',
    description: 'Transformative design for Bel Air\'s gated ultra-luxury community estates and private residences.',
    image: 'https://readdy.ai/api/search-image?query=bel%20air%20luxury%20estate%20interior%20elegant%20formal%20living%20room%20cream%20champagne%20gold%20accents%20marble%20floors%20drapes%20chandelier%20sophisticated%20refined%20architectural%20interiors%20editorial&width=800&height=600&seq=bela01&orientation=landscape',
  },
  {
    name: 'Malibu',
    subtitle: 'Coastal Luxury',
    description: 'Oceanfront retreats where natural California beauty meets sophisticated, timeless interior design.',
    image: 'https://readdy.ai/api/search-image?query=malibu%20coastal%20luxury%20home%20interior%20ocean%20view%20light%20airy%20natural%20materials%20linen%20wood%20stone%20minimalist%20california%20beach%20house%20interior%20design%20editorial%20warm%20neutral&width=800&height=600&seq=malibu01&orientation=landscape',
  },
  {
    name: 'West Hollywood',
    subtitle: 'Design-Forward Living',
    description: 'Creative, refined interiors for West Hollywood\'s vibrant and design-conscious residential community.',
    image: 'https://readdy.ai/api/search-image?query=west%20hollywood%20luxury%20contemporary%20interior%20design%20living%20room%20bold%20sophisticated%20dark%20moody%20neutral%20velvet%20furniture%20modern%20editorial%20california%20residential%20design%20warm%20tones&width=800&height=600&seq=weho01&orientation=landscape',
  },
  {
    name: 'Pacific Palisades',
    subtitle: 'Prestigious Hillside Estates',
    description: 'Elevated homes with canyon-coastal character — where architectural distinction meets refined design vision.',
    image: 'https://readdy.ai/api/search-image?query=pacific%20palisades%20luxury%20hillside%20home%20interior%20california%20views%20warm%20natural%20wood%20stone%20open%20plan%20kitchen%20living%20area%20editorial%20architectural%20sophisticated%20neutral%20palette&width=800&height=600&seq=pp01&orientation=landscape',
  },
];

export default function LocalAreasSection() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => setTimeout(() => node.classList.add('revealed'), i * 80));
        obs.disconnect();
      }
    }, { threshold: 0.05 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white py-28">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="reveal text-center mb-16">
          <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Primary Service Areas</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-charcoal mt-3 leading-tight">
            Serving LA&apos;s Most Prestigious<br />
            <em className="not-italic text-stone-dark">Neighborhoods &amp; Communities</em>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-stone/15">
          {PRIMARY_AREAS.map((area, i) => (
            <Link
              key={area.name}
              to="/location#contact"
              className={`reveal bg-white group cursor-pointer overflow-hidden ${i < 3 ? `reveal-delay-${i + 1}` : ''}`}
            >
              <div className="w-full aspect-[4/3] overflow-hidden relative img-zoom">
                <img
                  src={area.image}
                  alt={`Interior design in ${area.name}, Los Angeles`}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/15 transition-all duration-400" />
                <div className="absolute top-4 left-4 bg-charcoal/70 backdrop-blur-sm px-3 py-1">
                  <span className="text-cream/70 text-[9px] tracking-wider uppercase font-sans">{area.subtitle}</span>
                </div>
              </div>
              <div className="p-7 border-t border-stone/10">
                <h3 className="font-display text-2xl font-light text-charcoal mb-2 group-hover:text-stone-dark transition-colors">
                  {area.name}
                </h3>
                <p className="text-charcoal/50 text-xs leading-relaxed font-sans mb-4">{area.description}</p>
                <div className="flex items-center gap-2 text-stone-dark text-[10px] font-sans tracking-wider uppercase group-hover:gap-4 transition-all duration-300">
                  Request Service <i className="ri-arrow-right-line" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
